/**
 * Lazy load scripts.
 *
 * @module app/components/DeferredDependencies
 */
import _ from 'lodash';
import jQuery from 'jquery';
import LazyLoadingScript from './LazyLoadingScript';
import DeviceDetector from './DeviceDetector';
import MessageBusTopics from '../../common/MessageBusTopics';
import Modernizr from '../../vendor/modernizr/modernizr-custom';

var dependencies = null;

/**
 * Function to load self initialising modules
 * @return {void}
 */
const loadModules = () => {
    import('./StreamingIconsManager');
};

/**
 * Set dependencies object.
 * Function is called on init process to make sure dependencies variables are defined
 *
 * @returns {void}
 */
var setDependencies = function readUrls() {

    var footerUrls = window.footer && window.footer.scripts || [];

    // In case any url variable is undefined lazyloading of element will be resolved immediately
    dependencies = {
        bsUrl: {
            url: window.whStatic.config.keystone.bsUrl,
        },
        betslipIntegrationUrl: {
            url: window.whStatic.config.keystone.betslipIntegrationUrl,
        },
        customCss: {
            type: 'css',
            url: window.whStatic.config.keystone.customCss,
        },
        lazyMobileCss: {
            type: 'css',
            url: window.newMobileFullCss,
        },
        magnificPopup: {
            url: window.magnificPopupUrl,
        },
        footer: {
            urls: footerUrls,
        },
    };

    // Please remove once we drop support of iOS 9.2, Android 4.4 and IE11 (cheers!)
    if (
        !Modernizr.picture &&
        window.whStatic.config.fragmentCdnHost &&
        window.whStatic.config.fragmentCdnPath
    ) {

        var pictureFillUrl = window.whStatic.config.fragmentCdnHost +
            window.whStatic.config.fragmentCdnPath +
            '/js/vendor/polyfills/picturefill.min.js';

        dependencies.pictureFill = { url: pictureFillUrl };
    }
};

/**
 * Load script if provided url exists
 *
 * @param {String} url
 * @param {Function} [callback]
 *
 * @returns {void}
 *
 * @private
 */
var load = function load(url, callback) {
    if (url) {

        LazyLoadingScript.load(url, callback);
    }
};

/**
 * WHTV loader, it will load the new whtv player resources.
 *
 * @returns {void}
 *
 * @public
 */
var loadWHTV = function loadWHTV() {

    if (window.whStatic.config.whTV &&
        window.whStatic.config.whTV.enabled &&
        DeviceDetector.isDesktop() &&
        window.WH.sportsbook.frontEndConfig.dependencies.whtv &&
        window.WH.sportsbook.frontEndConfig.dependencies.whtv.js &&
        window.WH.sportsbook.frontEndConfig.dependencies.whtv.css) {

        load({
            type: 'css',
            url: window.whStatic.config.whTV.assetsURL + window.WH.sportsbook.frontEndConfig.dependencies.whtv.css,
        });

        load(window.whStatic.config.whTV.assetsURL + window.WH.sportsbook.frontEndConfig.dependencies.whtv.js);
    }
};

/**
 * Set up broadcasting of the current URL to the parent page when sportsbook is in an iframe
 *
 * @param hostname Hostname of the parent page including protocol (i.e. https://portal.williamhill.us)
 */
var broadcastNavigationEventsToParent = function broadcastNavigationEventsToParent(hostname){
    var events = [
        MessageBusTopics.NAVIGATION_PAGE_CHANGED,
        MessageBusTopics.NAVIGATION_SNIPPET_CHANGED,
        MessageBusTopics.SEARCH_OPEN,
        MessageBusTopics.SEARCH_CLOSE,
    ];
    if(window.top !== window.self) {
        for (var i = 0; i < events.length; i++) {
            var event = events[i];
            window.WH.messageBus.subscribe(event, function () {
                window.parent.postMessage(
                    {
                        type: 'updateURL',
                        location: window.location.pathname + window.location.search + window.location.hash,
                        height: window.screen.height,
                    },
                    hostname
                );
            });
        }
    }
};

/**
 * Fixes issue with betslip and mobile menu not scrolling to the top when loaded in an iframe
 *
 * @param hostname Hostname of the parent page including protocol (i.e. https://portal.williamhill.us)
 */
var scrollOffCanvasToTop = function scrollOffCanvasToTop(hostname){
    var events = [
        MessageBusTopics.BETSLIP_OPENING,
        MessageBusTopics.SIDEBAR_NAVIGATION_OPEN,
    ];
    if(window.top !== window.self) {
        for (var i = 0; i < events.length; i++) {
            var event = events[i];
            window.WH.messageBus.subscribe(event, function () {
                window.parent.postMessage(
                    {
                        type: 'scrollToTop',
                    },
                    hostname
                );
            });
        }
    }
};

/**
 * Load all script without dependencies
 *
 * @returns {void}
 *
 * @public
 */
var init = async function init() {
    // If we're in an iframe we want to inform the parent of URL changes
    if (window.whStatic.config.portal.enabled){
        broadcastNavigationEventsToParent(window.whStatic.config.portal.url);
        // Gross hack for NJHH as innovation stack doesn't have cookie needed by Device Detector present
        // Ideally we'd be able to use DeviceDetector.isIOS()
        var _iOSDevice = !!navigator.platform.match(/iPhone|iPod|iPad/);
        if(_iOSDevice){
            scrollOffCanvasToTop(window.whStatic.config.portal.url);
        }
    }

    if (window.WH.sportsbook.frontEndConfig.lazyloadScripts) {
        setDependencies();
        LazyLoadingScript.loadAll([
            dependencies.customCss,
            dependencies.bsUrl,
            dependencies.betslipIntegrationUrl,
        ], BetSlipLoaded);

        LazyLoadingScript.load(dependencies.lazyMobileCss);

        load(dependencies.magnificPopup.url, function () {
            window.WH.messageBus.publishSticky(MessageBusTopics.DEFERRED_LOAD_MAGNIFIC_POPUP);
        });

        LazyLoadingScript.loadAllOneByOne(dependencies.footer.urls);
    }
    loadModules();
    loadWHTV();
};

var BetSlipLoaded = function BetSlipLoaded() {
    window.WH.messageBus.publish('betslip.events.init.success');
    window.WH.messageBus.publish(MessageBusTopics.BETSLIP_ADAPTER_GET_BETS);
    window.WH.messageBus.publish(MessageBusTopics.BETSLIP_INIT_COMPLETE);

    // sidesteps result from betSlipAdapter.js > getBetsFromApi for NJ jurisdiction
    jQuery('body').removeClass('betslip-loading');
};

/**
 * Public functions
 *
 * @type {Object}
 */
var api = {
    reInit: init,
    init: _.once(init),
};

export default api;
